<template>
  <div ref="menu" class="menu">
    <div class="header">
      <div class="header__left">
        <IconButton
          form="transparent"
          class="header__icon header__icon--no-padding-left"
          data-testid="burger-menu"
          :icon="isMegaNavOpen ? 'chevron-left' : 'cross'"
          @click="handleCrossClick()"
        />
      </div>

      <div class="header__middle"></div>

      <div class="header__right">
        <Currency
          :options="dropdownoptions"
          :selected="currencyCode"
          class="header__currency-dropdown"
          @changed="handleCurrencyChange"
        />
        <IconButton
          form="transparent"
          :tag="mq.mdPlus ? 'button' : 'a'"
          class="header__icon"
          data-testid="search"
          icon="search"
          :href="mq.mdPlus ? '' : '/search'"
          @click="mq.mdPlus ? setSearchOpen(true) : null"
        />
        <IconButton
          form="transparent"
          :tag="isLoggedIn ? 'a' : 'button'"
          class="header__icon"
          icon="account"
          :href="isLoggedIn ? accountUrl : ''"
          @click="!isLoggedIn && setAccountOpen(true)"
        />
        <IconButton
          form="transparent"
          class="header__icon"
          icon="bag"
          :notification-number="lineItemsCount"
          @click="setCartOpen(true)"
        />
      </div>
    </div>
    <div class="menu__content">
      <Navigation />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";

import IconButton from "@/components/IconButton.vue";
import Navigation from "@/components/navigation/Navigation.vue";
import DropDown from "@/components/DropDown.vue";
import scrollLock from "@/helpers/scroll-lock";

export default {
  name: "BurgerMenu",
  components: {
    IconButton,
    Navigation,
    Currency: DropDown,
  },
  inject: ["mq"],
  data() {
    return {
      dropdownoptions: [
        {
          value: "gbp",
          label: "GBP (£)",
        },
        {
          value: "eur",
          label: "EUR (€)",
        },
        {
          value: "usd",
          label: "USD ($)",
        },
        {
          value: "aud",
          label: "AUD ($)",
        },
      ],
    };
  },
  computed: {
    ...mapState("app", ["isLoggedIn", "isMegaNavOpen"]),
    ...mapGetters("cart", ["lineItemsCount"]),
    ...mapGetters("app", ["currencyCode"]),
    accountUrl() {
      return "/account";
    },
  },
  mounted() {
    scrollLock.enable();
  },
  unmounted() {
    scrollLock.disable();
  },
  methods: {
    ...mapActions("app", [
      "toggleMobileMenu",
      "closeMegaNav",
      "setAccountOpen",
      "changeCurrency",
    ]),
    ...mapActions("search", ["addSearchTerm", "setSearchOpen"]),
    ...mapActions("cart", ["setCartOpen"]),
    handleCurrencyChange(value) {
      this.changeCurrency(value);
    },
    handleCrossClick() {
      if (this.isMegaNavOpen) {
        // If mega nav is open, act as back button - just close mega nav
        this.closeMegaNav();
      } else {
        // If mega nav is not open, act as close button - close the entire menu
        this.closeMegaNav(); // Ensure mega nav is closed
        this.toggleMobileMenu();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.header {
  background-color: $color-white;
  display: flex;
  align-items: center;
  gap: 40px;
  @extend .container;
  padding-top: 8px;
  padding-bottom: 8px;

  &__middle {
    display: none;
  }

  &__left,
  &__right {
    flex: 1 0 auto;
  }

  &__left {
    display: flex;
    align-items: center;
  }

  &__right {
    display: flex;
    justify-content: flex-end;
    text-align: right;
  }

  &__icon {
    display: flex;
    align-items: center;
    font-size: 24px;
    line-height: 1;
    padding: 12px;

    &:last-child {
      margin-right: -15px;
    }

    &:first-child {
      margin-left: -15px;
    }
  }

  &__currency-dropdown {
    margin-right: 8px;
    min-width: 80px;

    :deep() {
      .button {
        padding: 8px 12px;
        min-height: 40px;
        border: 1px solid $color-grey-light;
        background: $color-white;
        justify-content: center; // Center the text since no arrow

        .text {
          @include editorial-body(12px);
          color: $color-black;
          text-align: center;
        }

        .icon {
          display: none; // Hide the dropdown arrow
        }
      }

      .dropdown__panel {
        position: absolute;
        right: 0;
        left: auto;
        min-width: 100px;
        margin-top: 4px;
        border: 1px solid $color-grey-light;
        background: $color-white;
        z-index: 10;
      }

      .dropdown__item-button {
        padding: 12px 16px;
        min-height: 40px;

        .label {
          @include editorial-body(12px);
        }
      }
    }
  }

  :deep(.button--icon--has-text .icon) {
    @include respond-below(xl) {
      margin-right: 0;
    }
  }

  &__logo {
    vertical-align: middle;
    width: 141px;
  }

  &__text {
    @include t__utility-label-20;
  }
}

.menu {
  position: fixed;
  background: $color-white;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: 390px;
  height: 100%;
  height: 100dvh;
  z-index: 5;
  display: flex;
  flex-direction: column;

  .header {
    flex: 0 0 auto;
  }

  &__content {
    flex: 1 1 auto;
    min-height: 0;
    padding-top: 8px;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  &__bottom {
    flex: 0 0 auto;
    background-color: $color-grey-light;
    border-top: 1px solid rgba($color-grey-dark, 0.2);
    display: flex;
    padding: 0 10px;
    justify-content: space-evenly;
  }

  &__button {
    padding: 17px 0;
    flex: 0 0 auto;
    background: none;
    outline: none;
    border: none;
    font-size: 10px;
    opacity: 0.4;
    text-decoration: none;

    &.router-link-active {
      opacity: 1;
    }
  }

  &__header {
    flex: 0 0 auto;
    background: $color-grey-light;
    padding-top: 16px;
    padding-bottom: 16px;

    .heading {
      font-size: 16px;
      text-transform: uppercase;
      font-family: $heading-font-family;
    }
  }
}

.parent-route {
  margin-top: 14px;
  background-color: $color-white;
}

.back-button {
  font-family: $body-font-family;
  font-size: 10px;
  display: block;
  width: 100%;
  text-decoration: none;
  padding: 14px 0;

  .icon-arrow-left {
    margin-right: 10px;
  }
}
</style>
